import { IDetailsRowProps } from '@fluentui/react';
import {
  DetailsList,
  EmptyMessage,
  IColumnWithDataType,
  IDetailsListProps,
  listCellStylesNoLeftRightPadding,
  ListRow,
  listRowStylesWidgetLine,
  Loader,
  loaderStylesSpinnerXLarge,
  useClassNames,
} from '@h2oai/ui-kit';
import { useHistory } from 'react-router-dom';

import { useQueryRecentlyOpenedFeatureSets } from '../../featurestore/featurestore.hooks';
import { RecentlyUsedFeatureSet } from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { RoutePaths } from '../../pages/Routes';
import { formatDefaultDateAndTimeWithSeconds } from '../../utils/utils';
import {
  IRecentlyOpenedFeatureSetListClassNames,
  IRecentlyOpenedFeatureSetListStyles,
  recentlyOpenedFeatureSetListStylesDefault,
} from './RecentlyOpenedFeatureSetList.styles';

const columns: IColumnWithDataType[] = [
  {
    key: 'featureSetName',
    name: 'Feature Set Name',
    fieldName: 'featureSetDescription',
    minWidth: 200,
    data: {
      headerFieldName: 'featureSetName',
      listCellProps: {
        emptyMessage: 'No Description',
        styles: listCellStylesNoLeftRightPadding,
      },
    },
  },
  {
    key: 'lastAccessAt',
    name: 'Last Access',
    fieldName: 'lastAccessAt',
    minWidth: 150,
    data: {
      listCellProps: {
        onRenderText: (item: RecentlyUsedFeatureSet) => (
          <>{formatDefaultDateAndTimeWithSeconds(item.lastAccessAt) || `-`}</>
        ),
        styles: listCellStylesNoLeftRightPadding,
      },
    },
  },
  {
    key: 'updatedAt',
    name: 'Updated On',
    fieldName: 'updatedAt',
    minWidth: 150,
    data: {
      listCellProps: {
        onRenderText: (item: RecentlyUsedFeatureSet) => (
          <>{formatDefaultDateAndTimeWithSeconds(item.updatedAt) || `-`}</>
        ),
        styles: listCellStylesNoLeftRightPadding,
      },
    },
  },
];

export const RecentlyOpenedFeatureSetList = () => {
  const recentlyOpenedFeatureSetsQuery = useQueryRecentlyOpenedFeatureSets({
    meta: {
      toastError: (err: unknown) =>
        getErrorMessageBarMessage(err, `An error has occurred while loading the recently opened feature sets: `)
          .message,
    },
  });
  const recentlyOpenedFeatureSets = recentlyOpenedFeatureSetsQuery.data?.featureSets;
  const history = useHistory();
  const detailsListProps: IDetailsListProps = {
    columns,
    isHeaderVisible: false,
    items: recentlyOpenedFeatureSets || [],
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns = [] } = props || {};
      return (
        <ListRow
          styles={listRowStylesWidgetLine}
          data={item}
          columns={columns}
          loading={item.isNotLoaded}
          onClick={(e) => {
            history.push(`${RoutePaths.FEATURESTORE_FEATURE_SET}/${item.featureSetId}`);
            e?.preventDefault();
          }}
        />
      );
    },
  };
  const classNames = useClassNames<IRecentlyOpenedFeatureSetListStyles, IRecentlyOpenedFeatureSetListClassNames>(
    'RecentlyOpenedFeatureSetList',
    recentlyOpenedFeatureSetListStylesDefault
  );

  return (
    <div className={classNames.root} data-test="recently-opened-feature-set-list">
      {!recentlyOpenedFeatureSets ? (
        <Loader styles={loaderStylesSpinnerXLarge} label="Loading the recently opened feature sets..." />
      ) : recentlyOpenedFeatureSets.length ? (
        <div className={classNames.list} data-is-scrollable="true">
          <DetailsList {...detailsListProps} />
        </div>
      ) : (
        <EmptyMessage message="You don't have any recently opened feature sets" />
      )}
    </div>
  );
};
